import ShareableCardGenerator from "./ShareableCardGenerator";
import { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Gift, Copy, Users, Trophy, Share2, Check, Loader2, Clock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralSection = () => {
  const [referralData, setReferralData] = useState(null);
  const [stats, setStats] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const [codeRes, statsRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/referrals/my-code`),
        axios.get(`${API}/referrals/stats`),
        axios.get(`${API}/referrals/rewards`)
      ]);
      setReferralData(codeRes.data);
      setStats(statsRes.data);
      setRewards(rewardsRes.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      const shareUrl = `${window.location.origin}/?ref=${referralData?.code}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareReferral = async () => {
    const shareUrl = `${window.location.origin}/?ref=${referralData?.code}`;
    const shareText = rewards?.share_message || 'Join me on iStudyPro! Use my referral code to get 7 days of free premium. 📚✨';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join iStudyPro',
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const claimReferral = async () => {
    if (!claimCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    try {
      setClaiming(true);
      const response = await axios.post(`${API}/referrals/claim`, {
        referral_code: claimCode.trim()
      });
      toast.success(response.data.message);
      setClaimCode('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim referral code');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <Card className="p-6  border-2">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.6 }}
    >
      <Card className="p-6  border-2" data-testid="referral-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
            <Gift className="w-6 h-6 text-pink-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Invite Friends</h2>
            <p className="text-sm text-muted-foreground">Earn rewards for every friend who joins</p>
          </div>
        </div>

        {/* Referral Code Display */}
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 mb-6">
          <p className="text-sm text-muted-foreground mb-2">Your Referral Code</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-background/80 backdrop-blur rounded-xl p-4 border-2 border-dashed border-primary/30">
              <span className="text-2xl font-bold tracking-wider" data-testid="referral-code">
                {referralData?.code || '---'}
              </span>
            </div>
            <Button aria-label="Confirm"
              variant="outline"
              size="icon"
              onClick={copyToClipboard}
              className="h-14 w-14 rounded-xl border-2"
              data-testid="copy-referral-btn"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          
          <Button
            onClick={shareReferral}
            className="w-full mt-4 rounded-xl h-12 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
            data-testid="share-referral-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share with Friends
          </Button>
          
          {/* Shareable Card Generator */}
          <ShareableCardGenerator referralCode={referralData?.code} />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-muted/30 rounded-xl p-4 text-center">
            <Users className="w-5 h-5 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Successful</p>
          </div>
          <div className="bg-muted/30 rounded-xl p-4 text-center">
            <Clock className="w-5 h-5 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold">{stats?.pending_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div className="bg-muted/30 rounded-xl p-4 text-center">
            <Sparkles className="w-5 h-5 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold">{stats?.rewards_earned?.xp_earned || 0}</p>
            <p className="text-xs text-muted-foreground">XP Earned</p>
          </div>
          <div className="bg-muted/30 rounded-xl p-4 text-center">
            <Trophy className="w-5 h-5 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{stats?.rewards_earned?.premium_days || 0}</p>
            <p className="text-xs text-muted-foreground">Premium Days</p>
          </div>
        </div>

        {/* Rewards Info */}
        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Gift className="w-4 h-4 text-green-500" />
            Referral Rewards
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-background/50 rounded-lg p-3">
              <p className="font-medium text-green-600 mb-1">You Get (Referrer)</p>
              <p className="text-muted-foreground">{rewards?.referrer_rewards?.description}</p>
            </div>
            <div className="bg-background/50 rounded-lg p-3">
              <p className="font-medium text-blue-600 mb-1">Friend Gets (Referee)</p>
              <p className="text-muted-foreground">{rewards?.referee_rewards?.description}</p>
            </div>
          </div>
        </div>

        {/* Claim Referral Code */}
        <div className="border-t pt-6">
          <h3 className="font-semibold mb-3">Have a Referral Code?</h3>
          <div className="flex gap-2">
            <Input
              placeholder="Enter referral code"
              value={claimCode}
              onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
              className="flex-1 rounded-xl uppercase"
              maxLength={8}
              data-testid="claim-code-input"
            />
            <Button
              onClick={claimReferral}
              disabled={claiming || !claimCode.trim()}
              className="rounded-xl px-6"
              data-testid="claim-code-btn"
            >
              {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Claim'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Enter a friend's code to get {rewards?.referee_rewards?.premium_days || 7} days free premium!
          </p>
        </div>

        {/* Recent Referrals */}
        {stats?.referrals && stats.referrals.length > 0 && (
          <div className="border-t pt-6 mt-6">
            <h3 className="font-semibold mb-3">Recent Referrals</h3>
            <div className="space-y-2">
              {stats.referrals.slice(0, 5).map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium">{ref.referee_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(ref.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default ReferralSection;
